import {
  collection,
  query,
  where,
  getDocs,
  type DocumentData,
  type QuerySnapshot,
  limit,
  DocumentSnapshot,
  DocumentReference,
  doc,
  getDoc,
  Query,
  orderBy,
  startAfter,
  updateDoc,
  Timestamp,
  serverTimestamp,
  onSnapshot,
  QueryStartAtConstraint,
} from 'firebase/firestore';
import { db } from '../../config';
import {
  ocrStatus,
  processingStatus,
  registerStatus,
  resultStatus,
} from '../../../../constants/enum';
import { UseFormWatch } from 'react-hook-form';
import { AiOcrResultFiledType } from '../../../../validation/aiocrResult/aiOcrResultType';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

import { WatchSearchItemsType } from '../../../../components/uiParts/AiOcrSearchForm';
import { getInputsByIds } from '../input';
import { fusejsSearch, SearchableItem } from '../../fusejs';

export const resultsRef = collection(db, 'results');
const resultDocument = (id: string): DocumentReference<DocumentData> => doc(resultsRef, id);
export const resultSnapshot = async (id: string): Promise<DocumentSnapshot> =>
  await getDoc(resultDocument(id));

export const getResult = async (id: string): Promise<DocumentData | undefined> => {
  const result = await resultSnapshot(id);
  return result.data();
};

// 共通の型定義
type ResultCallback = (
  results: DocumentData[],
  lastVisible: DocumentSnapshot | null,
  isLastPage: boolean,
) => void;

// groupId パラメータの型
// undefined=フィルタなし, null=個人(groupId==null), string=特定グループ, (string|null)[]=複数グループ
export type GroupIdFilter = string | null | (string | null)[] | undefined;

// 個人画面用フィルタ（グループ所属ユーザー）:
// 自分の全レコード + 所属グループのレコード + groupId=null のレコード
export type PersonalFilter = {
  createdBy: string; // uid
  groupIds: string[]; // 所属グループIDの配列
};

// クエリ引数の統合型
export type ResultFilter = GroupIdFilter | PersonalFilter;

export const isPersonalFilter = (filter: ResultFilter): filter is PersonalFilter =>
  typeof filter === 'object' &&
  filter !== null &&
  !Array.isArray(filter) &&
  'createdBy' in filter &&
  !!(filter as PersonalFilter).createdBy;

// 共通クエリ
const buildQuery = (
  status: number | null,
  watchItems?: WatchSearchItemsType,
  sortDirection: 'asc' | 'desc' = 'asc',
  groupId?: GroupIdFilter,
  createdBy?: string,
): Query => {
  let q: Query = query(resultsRef, where('status', '==', resultStatus.valid));

  // groupId による絞り込み
  if (groupId !== undefined) {
    if (Array.isArray(groupId)) {
      // 複数グループの結果を取得（個人画面：所属グループ + null）
      q = query(q, where('groupId', 'in', groupId));
    } else {
      q = query(q, where('groupId', '==', groupId));
    }
  }

  // status による絞り込み
  if (status !== null) {
    if (status === processingStatus.draft || status === processingStatus.processing) {
      q = query(
        q,
        where('processing_status', 'in', [processingStatus.draft, processingStatus.processing]),
      );
    } else {
      q = query(q, where('processing_status', '==', status));
    }
  }

  // created_at でのソート
  q = query(q, orderBy('created_at', sortDirection));

  // 日付による絞り込み
  if (watchItems) {
    if (watchItems.startDate) {
      watchItems.startDate = dayjs(watchItems.startDate);
    }
    if (watchItems.endDate) {
      watchItems.endDate = dayjs(watchItems.endDate);
    }
  }
  if (watchItems?.startDate && dayjs.isDayjs(watchItems?.startDate)) {
    if (dayjs(watchItems.startDate).isValid()) {
      q = query(q, where('created_at', '>=', watchItems.startDate.toDate()));
    }
  }

  if (watchItems?.endDate && dayjs.isDayjs(watchItems?.endDate)) {
    if (dayjs(watchItems.endDate).isValid()) {
      const endDate = dayjs(watchItems.endDate).endOf('day');
      q = query(q, where('created_at', '<=', endDate.toDate()));
    }
  }

  // 設定年月日による絞り込み
  if (watchItems) {
    if (watchItems.establishmentStartDate) {
      watchItems.establishmentStartDate = dayjs(watchItems.establishmentStartDate);
    }
    if (watchItems.establishmentEndDate) {
      watchItems.establishmentEndDate = dayjs(watchItems.establishmentEndDate);
    }
  }

  // 修正: Firestoreに格納されている日付文字列を正しく比較するためにフォーマットを適用
  if (watchItems?.establishmentStartDate && dayjs.isDayjs(watchItems?.establishmentStartDate)) {
    if (dayjs(watchItems.establishmentStartDate).isValid()) {
      const establishmentStartDate = dayjs(watchItems.establishmentStartDate).format('YYYY/MM/DD');
      q = query(q, where('result_data.establishmentDate.value', '>=', establishmentStartDate));
    }
  }

  if (watchItems?.establishmentEndDate && dayjs.isDayjs(watchItems?.establishmentEndDate)) {
    if (dayjs(watchItems.establishmentEndDate).isValid()) {
      const establishmentEndDate = dayjs(watchItems.establishmentEndDate).format('YYYY/MM/DD');
      q = query(q, where('result_data.establishmentDate.value', '<=', establishmentEndDate));
    }
  }

  // 作成者による絞り込み（個人画面の自分のレコード取得用）
  // createdBy指定時は担当者検索と競合するためcreatedByを優先
  if (createdBy) {
    q = query(q, where('created_by', '==', createdBy));
  } else if (status && watchItems?.responsiblePerson) {
    // 担当者名（ステータス→下書き・未処理：作成者、処理済・出力済：更新者）
    q = query(q, where('created_by', '==', watchItems?.responsiblePerson));
  }

  return q;
};

// 共通の結果処理関数
const processResults = async (
  querySnapshot: QuerySnapshot,
  pageSize: number,
  watchItems?: WatchSearchItemsType,
): Promise<{
  results: DocumentData[];
  newLastVisible: DocumentSnapshot | null;
  isLastPage: boolean;
}> => {
  // Firestoreから取得した全ドキュメントスナップショット
  const allDocs: DocumentSnapshot<DocumentData>[] = querySnapshot.docs;
  // フィルタリング後の対象となるドキュメントスナップショットを格納する変数
  let targetDocs = allDocs;

  // 所有者名またはマンション名のあいまい検索が指定されている場合
  const needsFuseSearch = watchItems?.debtorName || watchItems?.mansionName;
  if (needsFuseSearch) {
    // fusejsSearchはDocumentDataの配列を要求するため、検索用のデータ配列を作成
    let candidatesForSearch: SearchableItem[] = allDocs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));

    // 所有者名のあいまい検索
    if (watchItems?.debtorName) {
      candidatesForSearch = fusejsSearch(
        candidatesForSearch,
        watchItems.debtorName,
        'result_data.debtorName.value',
      );
    }

    // マンション名のあいまい検索
    if (watchItems?.mansionName) {
      candidatesForSearch = fusejsSearch(
        candidatesForSearch,
        watchItems.mansionName,
        'result_data.mansionName.value',
      );
    }

    // あいまい検索の結果に一致したドキュメントのIDセットを作成
    const matchedIds = new Set(candidatesForSearch.map(m => m.id));

    // IDセットを元に、元のドキュメントスナップショット配列から対象を絞り込む
    targetDocs = allDocs.filter(docSnap => matchedIds.has(docSnap.id));
  }

  // ページング
  // フィルタリング後のドキュメントリストから、現在のページに表示する分だけを切り出す
  const pagedDocs = targetDocs.slice(0, pageSize);

  // 表示用に取得
  // ページに表示するドキュメント（pagedDocs）のinputを一括取得
  const inputIds = pagedDocs
    .map(docSnap => (docSnap.data() as DocumentData).inputId)
    .filter((id): id is string => !!id);
  const inputMap = await getInputsByIds(inputIds);

  const results: DocumentData[] = pagedDocs.map(docSnap => {
    const d = docSnap.data() as DocumentData;
    return { id: docSnap.id, ...d, input: inputMap[d.inputId] };
  });

  const newLastVisible = targetDocs.length > pageSize ? targetDocs[pageSize - 1] : null;

  // 最終ページかどうかを判定
  const isLastPage = targetDocs.length <= pageSize;

  return { results, newLastVisible, isLastPage };
};

// DocumentSnapshot配列を重複排除して created_at 降順にソート
const deduplicateAndSort = (
  ...docArrays: DocumentSnapshot<DocumentData>[][]
): DocumentSnapshot<DocumentData>[] => {
  const seen = new Set<string>();
  const merged: DocumentSnapshot<DocumentData>[] = [];
  for (const docs of docArrays) {
    for (const docSnap of docs) {
      if (!seen.has(docSnap.id)) {
        seen.add(docSnap.id);
        merged.push(docSnap);
      }
    }
  }
  merged.sort((a, b) => {
    const aTime = (a.data() as DocumentData)?.created_at?.toMillis?.() ?? 0;
    const bTime = (b.data() as DocumentData)?.created_at?.toMillis?.() ?? 0;
    return bTime - aTime;
  });
  return merged;
};

// 複数のQuerySnapshotをマージし、重複排除して created_at 降順にソートしたドキュメント配列を返す
const mergeAndDedup = (...snapshots: QuerySnapshot[]): DocumentSnapshot<DocumentData>[] => {
  return deduplicateAndSort(...snapshots.map(snap => snap.docs));
};

// ページング前に全件へあいまい検索を適用する（ページング後だと最新N件に検索範囲が限定されてしまう）
const filterDocsBySearch = (
  docs: DocumentSnapshot<DocumentData>[],
  watchItems?: WatchSearchItemsType,
): DocumentSnapshot<DocumentData>[] => {
  if (!watchItems?.debtorName && !watchItems?.mansionName) return docs;

  let candidates: SearchableItem[] = docs.map(docSnap => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  if (watchItems?.debtorName) {
    candidates = fusejsSearch(candidates, watchItems.debtorName, 'result_data.debtorName.value');
  }
  if (watchItems?.mansionName) {
    candidates = fusejsSearch(candidates, watchItems.mansionName, 'result_data.mansionName.value');
  }

  const matchedIds = new Set(candidates.map(c => c.id));
  return docs.filter(docSnap => matchedIds.has(docSnap.id));
};

// PersonalFilter用: 2クエリを実行してマージしたドキュメント配列を返す
// 1. created_by == uid（自分の全レコード。グループ機能前に作成したOCRも含む）
// 2. groupId in [所属グループIDs]（所属グループのレコード）
const fetchPersonalDocs = async (
  filter: PersonalFilter,
  status: number | null,
  searchData?: WatchSearchItemsType,
  sortDirection: 'asc' | 'desc' = 'desc',
): Promise<DocumentSnapshot<DocumentData>[]> => {
  const myQuery = buildQuery(status, searchData, sortDirection, undefined, filter.createdBy);

  const queries: Promise<QuerySnapshot>[] = [getDocs(myQuery)];

  if (filter.groupIds.length > 0) {
    const groupQuery = buildQuery(status, searchData, sortDirection, filter.groupIds);
    queries.push(getDocs(groupQuery));
  }

  const snapshots = await Promise.all(queries);
  return mergeAndDedup(...snapshots);
};

// 指定ユーザーの本日のアップロード数を取得
export const getDailyUploadCount = async (userId: string): Promise<number> => {
  const todayStart = dayjs().tz('Asia/Tokyo').startOf('day').toDate();
  const q = query(
    resultsRef,
    where('created_by', '==', userId),
    where('status', '==', resultStatus.valid),
    where('created_at', '>=', todayStart),
  );
  const querySnapshot: QuerySnapshot = await getDocs(q);
  return querySnapshot.size;
};

// CSV取得用（検索結果データを全件取得）
export const getResultsForTsv = async (
  status: number,
  watchItems?: WatchSearchItemsType,
  resultFilter?: ResultFilter,
): Promise<DocumentData[]> => {
  const statusMap = {
    [processingStatus.draft]: [processingStatus.draft, processingStatus.processing],
    [processingStatus.processing]: [processingStatus.draft, processingStatus.processing],
    [processingStatus.completed]: [processingStatus.completed],
    [processingStatus.outputted]: [processingStatus.outputted],
  };
  const processingStatuses = statusMap[status] || [];

  let allDocs: DocumentSnapshot<DocumentData>[];

  if (isPersonalFilter(resultFilter)) {
    allDocs = await fetchPersonalDocs(resultFilter, status, watchItems, 'desc');
    // buildQueryでprocessing_statusの基本条件は付与済みだが、
    // statusMapとの差異を防ぐためクライアントサイドでも明示的にフィルタ
    allDocs = allDocs.filter(docSnap => {
      const ps = (docSnap.data() as DocumentData)?.processing_status;
      return processingStatuses.includes(ps);
    });
  } else {
    let q = buildQuery(status, watchItems, 'desc', resultFilter);
    q = query(q, where('processing_status', 'in', processingStatuses));
    const querySnapshot = await getDocs(q);
    allDocs = querySnapshot.docs;
  }

  let results: DocumentData[] = [];
  for (const docSnap of allDocs) {
    results.push({ id: docSnap.id, ...docSnap.data() });
  }

  // 所有者名のあいまい検索
  if (watchItems?.debtorName) {
    results = fusejsSearch(
      results.map(r => ({ ...r, id: r.id ?? '' })),
      watchItems.debtorName,
      'result_data.debtorName.value',
    );
  }

  // マンション名のあいまい検索
  if (watchItems?.mansionName) {
    results = fusejsSearch(
      results.map(r => ({ ...r, id: r.id ?? '' })),
      watchItems.mansionName,
      'result_data.mansionName.value',
    );
  }

  return results;
};

// リアルタイムで取得しないページネーション関数（文字読み取り結果画面の「次へ」「前へ」）
export const getResultsPaginatedNonRealtime = async (
  status: number | null,
  currentDocSnap: DocumentSnapshot | null = null,
  searchData?: WatchSearchItemsType,
  resultFilter?: ResultFilter,
): Promise<{
  nextResultId: string | null;
  prevResultId: string | null;
}> => {
  // PersonalFilter または 検索条件がある場合は全件取得してクライアントサイドでフィルタリング
  const hasSearchConditions =
    searchData?.debtorName ||
    searchData?.mansionName ||
    searchData?.establishmentStartDate ||
    searchData?.establishmentEndDate ||
    searchData?.responsiblePerson ||
    searchData?.startDate ||
    searchData?.endDate;

  if (isPersonalFilter(resultFilter) || hasSearchConditions) {
    let allDocs: DocumentSnapshot<DocumentData>[];

    if (isPersonalFilter(resultFilter)) {
      allDocs = await fetchPersonalDocs(resultFilter, status, searchData, 'desc');
    } else {
      const baseQuery = buildQuery(status, searchData, 'desc', resultFilter);
      const querySnapshot = await getDocs(baseQuery);
      allDocs = querySnapshot.docs;
    }

    const allResults: DocumentData[] = allDocs.map(docSnap => {
      const d = docSnap.data() as DocumentData;
      return { id: docSnap.id, ...d };
    });

    // あいまい検索がある場合はfuse.jsで検索を実行
    let filteredResults = allResults;
    if (searchData?.debtorName) {
      filteredResults = fusejsSearch(
        filteredResults.map(r => ({ ...r, id: r.id ?? '' })),
        searchData.debtorName,
        'result_data.debtorName.value',
      );
    }
    if (searchData?.mansionName) {
      filteredResults = fusejsSearch(
        filteredResults.map(r => ({ ...r, id: r.id ?? '' })),
        searchData.mansionName,
        'result_data.mansionName.value',
      );
    }

    // フィルタリング後の順序でDocumentSnapshotを並び替え
    allDocs = filteredResults
      .map(result => allDocs.find(doc => doc.id === result.id))
      .filter((doc): doc is DocumentSnapshot<DocumentData> => doc !== undefined);

    // 現在のドキュメントのインデックスを取得
    const currentIndex = allDocs.findIndex(doc => doc.id === currentDocSnap?.id);

    if (currentIndex === -1) {
      return {
        nextResultId: null,
        prevResultId: null,
      };
    }

    const nextResultId = currentIndex < allDocs.length - 1 ? allDocs[currentIndex + 1].id : null;
    const prevResultId = currentIndex > 0 ? allDocs[currentIndex - 1].id : null;

    return {
      nextResultId,
      prevResultId,
    };
  }

  // 検索条件がない場合は従来の処理
  const groupId = resultFilter as GroupIdFilter;
  const nextQuery = buildQuery(status, searchData, 'desc', groupId);
  const nextQueryWithLimit = query(nextQuery, startAfter(currentDocSnap), limit(1));
  const prevQuery = buildQuery(status, searchData, 'asc', groupId);
  const prevQueryWithLimit = query(prevQuery, startAfter(currentDocSnap), limit(1));
  const [nextSnapshot, prevSnapshot] = await Promise.all([
    getDocs(nextQueryWithLimit),
    getDocs(prevQueryWithLimit),
  ]);
  const nextResultId = nextSnapshot.docs[0]?.id ?? null;
  const prevResultId = prevSnapshot.docs[0]?.id ?? null;
  return {
    nextResultId,
    prevResultId,
  };
};

//最初のページはonSnapshotで監視
export const getResultsPaginatedRealtimeFirstPage = (
  status: number | null,
  pageSize = 50,
  callback: ResultCallback,
  watchItems?: WatchSearchItemsType,
  resultFilter?: ResultFilter,
): (() => void) => {
  const needsFuseSearch = watchItems?.debtorName || watchItems?.mansionName;

  // PersonalFilter: 複数リスナーでマージ
  if (isPersonalFilter(resultFilter)) {
    let myDocs: DocumentSnapshot<DocumentData>[] = [];
    let groupDocs: DocumentSnapshot<DocumentData>[] = [];
    const hasGroups = resultFilter.groupIds.length > 0;

    // 全リスナーの初回取得完了を追跡
    const totalListeners = hasGroups ? 2 : 1;
    let readyCount = 0;
    let mergeTimer: ReturnType<typeof setTimeout> | null = null;

    const handleMerge = async (): Promise<void> => {
      const merged = deduplicateAndSort(myDocs, groupDocs);

      // あいまい検索はページング前の全件に対して適用する
      const filteredDocs = filterDocsBySearch(merged, watchItems);

      // ページサイズ分だけ切り出し
      const pagedDocs = filteredDocs.slice(0, pageSize);
      const inputIds = pagedDocs
        .map(docSnap => (docSnap.data() as DocumentData).inputId)
        .filter((id): id is string => !!id);
      const inputMap = await getInputsByIds(inputIds);

      const results: DocumentData[] = pagedDocs.map(docSnap => {
        const d = docSnap.data() as DocumentData;
        return { id: docSnap.id, ...d, input: inputMap[d.inputId] };
      });

      const newLastVisible = filteredDocs.length > pageSize ? filteredDocs[pageSize - 1] : null;
      const isLastPage = filteredDocs.length <= pageSize;
      callback(results, newLastVisible, isLastPage);
    };

    // 初回は全リスナー完了後に実行、以降はdebounceで短時間の連続更新を集約
    const scheduleMerge = (): void => {
      if (readyCount < totalListeners) {
        readyCount++;
        if (readyCount < totalListeners) return;
      }
      // debounce: 短時間に複数リスナーが更新された場合に集約
      if (mergeTimer) clearTimeout(mergeTimer);
      mergeTimer = setTimeout(() => {
        void handleMerge();
      }, 50);
    };

    const onError = (label: string) => (error: Error) => {
      // eslint-disable-next-line no-console
      console.error(`onSnapshot error (${label}):`, error);
      callback([], null, true);
    };

    // クエリ1: 自分のレコード（グループ機能前に作成したOCRも含む）
    const myQueryBase = buildQuery(status, watchItems, 'desc', undefined, resultFilter.createdBy);
    const myQueryFinal = needsFuseSearch ? myQueryBase : query(myQueryBase, limit(pageSize + 1));
    const unsubMy = onSnapshot(
      myQueryFinal,
      (snapshot: QuerySnapshot) => {
        myDocs = snapshot.docs;
        scheduleMerge();
      },
      onError('my'),
    );

    // クエリ2: 所属グループのレコード
    let unsubGroup: (() => void) | null = null;
    if (hasGroups) {
      const groupQueryBase = buildQuery(status, watchItems, 'desc', resultFilter.groupIds);
      const groupQueryFinal = needsFuseSearch
        ? groupQueryBase
        : query(groupQueryBase, limit(pageSize + 1));
      unsubGroup = onSnapshot(
        groupQueryFinal,
        (snapshot: QuerySnapshot) => {
          groupDocs = snapshot.docs;
          scheduleMerge();
        },
        onError('group'),
      );
    }

    return () => {
      if (mergeTimer) clearTimeout(mergeTimer);
      unsubMy();
      if (unsubGroup) unsubGroup();
    };
  }

  // 従来の処理（グループ画面・グループ未所属の個人画面）
  const qBase = buildQuery(status, watchItems, 'desc', resultFilter as GroupIdFilter);
  const qWithLimit = needsFuseSearch ? qBase : query(qBase, limit(pageSize + 1));

  const unsubscribe = onSnapshot(
    qWithLimit,
    async (querySnapshot: QuerySnapshot) => {
      const { results, newLastVisible, isLastPage } = await processResults(
        querySnapshot,
        pageSize,
        watchItems,
      );

      callback(results, newLastVisible, isLastPage);
    },
    error => {
      // eslint-disable-next-line no-console
      console.error('onSnapshot error:', error);
      callback([], null, true);
    },
  );

  return unsubscribe;
};

// resultsレコードを削除
export const deleteResult = async (id: string): Promise<void> => {
  const resultDocRef = resultDocument(id);
  await updateDoc(resultDocRef, { status: registerStatus.deleted, deleted_at: serverTimestamp() });
};

// resultドキュメントの更新
export const handleResultsProcess = async (
  resultId: string,
  watch: UseFormWatch<AiOcrResultFiledType>,
  user?: { uid?: string } | null,
  status?: number | null,
  resultStatus?: number | null,
): Promise<void> => {
  const existingData = await getResult(resultId);
  if (!existingData) throw new Error('更新できませんでした。');

  const updateData: {
    processing_status?: number | null;
    status?: number | null;
    updated_at: Timestamp;
    updated_by: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    result_data: any;
  } = {
    processing_status: status,
    status: resultStatus,
    updated_at: Timestamp.now(),
    updated_by: user?.uid || '',
    result_data: { ...existingData.result_data },
  };

  const updateIfChanged = (
    field: keyof AiOcrResultFiledType,
    path: string,
    isNumber = false,
  ): void => {
    const value = watch(field);
    if (value !== undefined && value !== null) {
      updateData.result_data[path] = {
        ...updateData.result_data[path],
        value: isNumber ? Number(value) : value,
      };
    }
  };
  updateIfChanged('mansionName', 'mansionName');
  updateIfChanged('propertyAddress', 'propertyAddress');
  updateIfChanged('roomNumber', 'roomNumber');
  updateIfChanged('previousOwnerCorporateName', 'previousOwnerCorporateName');
  updateIfChanged('establishmentDate', 'establishmentDate');
  updateIfChanged('debtorAddress', 'debtorAddress');
  updateIfChanged('debtorName', 'debtorName');
  updateIfChanged('mortgageeCompanyName', 'mortgageeCompanyName');
  updateIfChanged('loanAmount', 'loanAmount', true);
  updateIfChanged('interestRate', 'interestRate', true);
  updateIfChanged('candidateCompanies', 'candidateCompanies');
  updateIfChanged('candidateCompaniesByGemini', 'candidateCompaniesByGemini');

  updateData.result_data.urls = existingData.result_data.urls;

  // 設定年月日の処理
  // watchから取得した日付をフォーマットして更新
  const establishmentDate = watch('establishmentDate');
  if (establishmentDate) {
    const formattedDate = dayjs(establishmentDate).format('YYYY/MM/DD');
    updateData.result_data.establishmentDate = {
      ...updateData.result_data.establishmentDate,
      value: formattedDate,
    };
  } else {
    updateData.result_data.establishmentDate = {
      ...updateData.result_data.establishmentDate,
      value: null,
    };
  }

  await updateDoc(doc(db, 'results', resultId), updateData);
};

//ページングはgetDocsで取得
export const getResultsPaginatedOneTime = async (
  status: number | null,
  pageSize = 50,
  lastVisible: DocumentSnapshot | null = null,
  watchItems?: WatchSearchItemsType,
  resultFilter?: ResultFilter,
): Promise<{
  results: DocumentData[];
  newLastVisible: DocumentSnapshot | null;
  isLastPage: boolean;
}> => {
  if (isPersonalFilter(resultFilter)) {
    // 個人画面: 3クエリでマージ（ページングはクライアントサイド）
    // NOTE: 毎回全件取得してクライアントでページングするため、レコード数増加時に
    // Firestoreの読み取りコストとレイテンシが線形増加する。将来的に要改善。
    const allDocs = await fetchPersonalDocs(resultFilter, status, watchItems, 'desc');

    // あいまい検索はページング前の全件に対して適用する
    const filteredDocs = filterDocsBySearch(allDocs, watchItems);

    // lastVisible以降のドキュメントを取得
    let startIndex = 0;
    if (lastVisible) {
      const lastIdx = filteredDocs.findIndex(d => d.id === lastVisible.id);
      if (lastIdx !== -1) startIndex = lastIdx + 1;
    }

    const pagedDocs = filteredDocs.slice(startIndex, startIndex + pageSize + 1);
    const targetDocs = pagedDocs.slice(0, pageSize);

    const inputIds = targetDocs
      .map(docSnap => (docSnap.data() as DocumentData).inputId)
      .filter((id): id is string => !!id);
    const inputMap = await getInputsByIds(inputIds);

    const results: DocumentData[] = targetDocs.map(docSnap => {
      const d = docSnap.data() as DocumentData;
      return { id: docSnap.id, ...d, input: inputMap[d.inputId] };
    });

    const newLastVisible = pagedDocs.length > pageSize ? targetDocs[targetDocs.length - 1] : null;
    const isLastPage = pagedDocs.length <= pageSize;

    return { results, newLastVisible, isLastPage };
  }

  // 従来の処理（グループ画面など）
  const qBase = buildQuery(status, watchItems, 'desc', resultFilter as GroupIdFilter);
  const paginatedQueryConstraints: QueryStartAtConstraint[] = [];

  if (lastVisible) {
    paginatedQueryConstraints.push(startAfter(lastVisible));
  }

  const paginatedQuery = query(qBase, ...paginatedQueryConstraints, limit(pageSize + 1));

  const querySnapshot = await getDocs(paginatedQuery);

  const { results, newLastVisible, isLastPage } = await processResults(
    querySnapshot,
    pageSize,
    watchItems,
  );

  return { results, newLastVisible, isLastPage };
};

// グループIDごとの処理中件数をリアルタイム監視
export const subscribeProcessingCountByGroup = (
  groupId: string,
  callback: (count: number) => void,
): (() => void) => {
  const q = query(
    resultsRef,
    where('groupId', '==', groupId),
    where('status', '==', resultStatus.valid),
    where('processing_status', '==', processingStatus.processing),
    where('ocr_status', '==', ocrStatus.processing),
  );
  return onSnapshot(q, snapshot => {
    callback(snapshot.size);
  });
};
